import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi, type PropertyGroupResponse } from '@/shared/api';
import { CommunicationUnsavedGuard } from '../communication-catalog/CommunicationUnsavedGuard';
import { EditorSaveBar } from '../communication-catalog/EditorSaveBar';
import { HouseRulesEditor } from './HouseRulesEditor';
import { HouseRulesPreview } from './HouseRulesPreview';
import {
  changedLanguages,
  rulesSummary,
  savedRules,
  type RulesDraft,
  type RulesLang,
} from './houseRulesModel';
import { useObjectDetail } from './ObjectDetailContext';
import { useObjectUpdate } from './useObjectUpdate';

/** Bereich „Hausordnung": gilt für alle Apartments, das Gastportal zeigt die passende Sprache. */
export function ObjectHouseRulesTab() {
  const { groupId } = useObjectDetail();
  const api = useApi();
  const { data: group } = useQuery({
    queryKey: ['property-groups', groupId],
    queryFn: () => api.propertyGroups.get(groupId),
  });
  if (!group) return null;
  // Neu eingehängt je Objekt: ein Entwurf gehört nie zu einem anderen Objekt.
  return <HouseRulesWorkspace key={group.id} group={group} />;
}

function HouseRulesWorkspace({ group }: { group: PropertyGroupResponse }) {
  const { canEdit } = useObjectDetail();
  const saved = savedRules(group);
  // Nur die angefasste Sprache ist Entwurf; die andere folgt dem Serverstand, auch wenn ihn
  // inzwischen jemand anders geändert hat.
  const [draft, setDraft] = useState<Partial<RulesDraft>>({});
  const [lang, setLang] = useState<RulesLang>('De');
  const current: RulesDraft = { De: draft.De ?? saved.De, En: draft.En ?? saved.En };
  const changed = changedLanguages(current, saved);
  const update = useObjectUpdate(group, '/property-groups/$id/hausordnung', () => setDraft({}));

  return (
    <section aria-labelledby="object-house-rules-title" className="space-y-4">
      <div>
        <h2 id="object-house-rules-title" className="text-ink font-serif text-2xl">
          Hausordnung
        </h2>
        <p className="text-ink-soft mt-1 max-w-3xl text-sm">
          Gilt für alle Apartments dieses Objekts. Gäste sehen die Fassung in ihrer Sprache.
        </p>
      </div>
      <div className="@container">
        <div className="@4xl:grid-cols-[minmax(0,1fr)_20rem] grid items-start gap-6">
          <HouseRulesEditor
            draft={current}
            saved={saved}
            lang={lang}
            // Während des Speicherns gesperrt: was dann noch getippt würde, ginge verloren.
            editable={canEdit && !update.saving}
            onLangChange={setLang}
            onChange={(language, value) => setDraft({ ...draft, [language]: value })}
          />
          <HouseRulesPreview
            draft={current}
            dirty={changed.length > 0}
            lang={lang}
            onLangChange={setLang}
          />
        </div>
      </div>
      {canEdit && (
        <EditorSaveBar
          count={changed.length}
          summary={rulesSummary(current, saved)}
          saving={update.saving}
          blocked={false}
          failure={
            update.error
              ? {
                  title: update.unanswered ? 'Keine Antwort' : 'Nicht gespeichert',
                  message: update.error,
                }
              : null
          }
          onDiscard={() => {
            setDraft({});
            update.reset();
          }}
          // Nur geänderte Sprachen: fehlend heißt für den Server „unverändert“, leer „löschen“.
          onSave={() =>
            update.save({
              ...(changed.includes('De') ? { houseRulesDe: current.De } : {}),
              ...(changed.includes('En') ? { houseRulesEn: current.En } : {}),
            })
          }
        />
      )}
      <CommunicationUnsavedGuard dirty={changed.length > 0} />
    </section>
  );
}
